import { useState } from 'react'
import { useAtomValue } from 'jotai'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { useNavigate } from "react-router-dom"
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import Button from '@mui/material/Button'

import { selectedMunicipalityIdAtom } from "@/state"
import LoadingBox from "@/components/loading-box"
import ErrorBox from "@/components/error-box"
import type {
    YearlyFeeDiscount,
} from '@/types'
import RemoveDialog from './remove-dialog'
import { DISCOUNT_TYPE_LABEL_MAP, HISTORY_RETURN_PARAM_KEY } from '@/constants/general'
import { convertFromCents, printPrice } from '@/helpers'
import { useYearlyFeeDiscounts } from '@/hooks/api/use-yearly-fee-discount'
import YearlyFeeDiscountActions from './actions'
import FeedbackAlerts from '@/components/feedback-alerts'
import YearlyFeeDiscountForm from '@/components/forms/yearly-fee-discount'
import YearlyFeeCollectionDiscountsMassActions from './mass-actions'

function YearlyFeeDiscountsList({
    year,
} : {
    year: number | string,
}) {

    const municipalityId = useAtomValue(selectedMunicipalityIdAtom)

    const navigate = useNavigate()

    const handleClick = (_event: React.MouseEvent<unknown>, id: number) => {
        navigate(`${id}?${HISTORY_RETURN_PARAM_KEY}=true`)
    }

    const [success, setSuccess] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [create, setCreate] = useState<boolean>(false)

    // Action dialogs
    const [remove, setRemove] = useState<YearlyFeeDiscount | null>(null)
    
    const { status, data, error: queryError } = useYearlyFeeDiscounts(municipalityId, year)

    if (status === 'pending') {
        return (
            <LoadingBox />
        )
    }

    if (queryError) {
        return <ErrorBox
            error={queryError}
            message={`Nepovedlo se načíst slevy obce s ID ${municipalityId}`}
        />
    }

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            overflowX: 'auto'
        }}>
            {data.length === 0 ?
                <Box px={2.5}>
                    Obec nemá nastavené žádné slevy pro rok {year}
                </Box>
            :
                <Box>
                    <FeedbackAlerts
                        success={success}
                        error={error}
                        onClearSuccess={() => setSuccess(null)}
                        onClearError={() => setError(null)}
                    />
                    <Table
                        size="small"
                        aria-label="Seznam slev"
                        sx={{
                            mt: 1,
                            minWidth: 600,
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ paddingLeft: 3 }}>Název</TableCell>
                                <TableCell>Typ</TableCell>
                                <TableCell>Výše (Kč)</TableCell>
                                <TableCell>Věk od</TableCell>
                                <TableCell>Věk do</TableCell>
                                <TableCell align="right"/>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map(row => (
                                <TableRow
                                    key={row.id}
                                    sx={{
                                        cursor: 'pointer',
                                        '&:last-child td, &:last-child th': { border: 0 },
                                    }}
                                    onClick={(event) => handleClick(event, row.id)}
                                    hover
                                >
                                    <TableCell component="th" scope="row" sx={{ paddingLeft: 3 }}>
                                        {row.name}
                                    </TableCell>
                                    <TableCell>
                                        <Box color="customGrey.secondaryText">
                                            {DISCOUNT_TYPE_LABEL_MAP[row.type]}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {row.set_amount_to_zero ? 'Kompletní osvobození' :printPrice(convertFromCents(row.amount_cents))}
                                    </TableCell>
                                    <TableCell>
                                        {row.age_from}
                                    </TableCell>
                                    <TableCell>
                                        {row.age_to}
                                    </TableCell>
                                    <TableCell align="right">
                                        <YearlyFeeDiscountActions
                                            yearlyFeeDiscount={row}
                                            setRemove={setRemove}
                                            setError={setError}
                                            allowDetail
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            }
            <Box px={2.5} py={1.5}>
                {create ?
                    <Box maxWidth="sm">
                        <YearlyFeeDiscountForm
                            year={year}
                            closeCallback={() => setCreate(false)}
                        />
                    </Box>
                    :
                    <Button
                        variant="contained"
                        color="terciary"
                        startIcon={<AddRoundedIcon />}
                        onClick={() => setCreate(true)}
                    >
                        Přidat novou slevu
                    </Button>
                }
            </Box>
            <Dialog
                key={'remove-'+remove?.id}
                open={!!remove}
                onClose={() => setRemove(null)}
            >
                <DialogContent>
                    <RemoveDialog
                        yearlyFeeDiscountId={remove?.id}
                        handleClose={() => setRemove(null)}
                    />
                </DialogContent>
            </Dialog>
            <YearlyFeeCollectionDiscountsMassActions />
        </Box>
    )
}

export default YearlyFeeDiscountsList
